// Import required dependencies
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";

// Import components
import ButtonIcon from "../../../components/ButtonIcon/ButtonIcon";
import Tabs from "../../../components/Tabs/Tabs";
import MerchantDetails from "./MerchantDetails/MerchantDetails";
import Files from "./Files/Files";
import FraudScore from "./FraudScore/FraudScore";
import DropDown from "../../../components/DropDown/DropDown";
import Submission from "./Submission/Submission";
import Activity from "./Activity/Activity";
import EditMobile from "./EditMobile/EditMobile";
import Spinner from "../../../components/Loaders/Spinner";
import SendMerchantLink from "./SendMerchantLink/SendMerchantLink";
import GenerateDownload from "./GenerateDownload/GenerateDownload";
import ChangeOwner from "./ChangeOwner/ChangeOwner";
import ConfirmationModal from "./ConfirmationModal/ConfirmationModal";
import EditEmail from "./EditEmail/EditEmail";
import AssignProcessor from "./AssignProcessor/AssignProcessor";
import { Copier } from "../../../../components/copier";
import AppStatusDropDown from "../../../components/AppStatusDropDown";
import CopyApplication from "./CopyApplication/Index";
import DownloadIcon from '../../../assets/svg/icons/common/Download';
import PaperPlane from "../../../assets/svg/icons/common/PaperPlane";
import AssignMSP from "./AssignMSP";
import EditZoho from "./EditZoho/EditZoho";

// Import styles
import style from "../Application.module.scss";

// Import API functions
import {
  getPreappById,
  getMerchantInfo,
  updateProgression,
  getNotes,
  deleteBulkApps,
  cancelMultipleApp,
  cancelSignature,
  resendSignature,
} from "../../../../api";

// Import icons
import CopyIcon from "../../../assets/svg/icons/common/copy";

// Import config
import config from "../../../../config";

// Import stores
import useCommonStore from "../../../stores/common";
import useApplicationStore from "../../../stores/application";
import useUserStore from "../../../stores/user";

// Import constants
const STRATUS_URL = config.paymentcloud.STRATUS_URL;
const MAIN_URL = config.paymentcloud.MAIN_URL;

// Define constants
const awaiting_signature_request = 6;
const app_pending_pc_approval = 15;
const Prevet_Application = 19;
const awaiting_signature = 7;
const hide_progression = [
  awaiting_signature_request,
  app_pending_pc_approval,
  awaiting_signature,
  Prevet_Application,
];

/**
 * ApplicationDetails component for displaying and managing application details
 * @component
 * @returns {JSX.Element} ApplicationDetails component
 */
function ApplicationDetails() {
  const expandDetails = useCommonStore((state) => state.expandDetails);
  const setShowDetails = useCommonStore((state) => state.setShowDetails);
  const toggleExpandDetails = useCommonStore(
    (state) => state.toggleExpandDetails,
  );
  const activeApp = useApplicationStore((state) => state.activeApp);
  const activeAppData = useApplicationStore((state) => state.activeAppData);
  const setActiveAppData = useApplicationStore(
    (state) => state.setActiveAppData,
  );
  const loggedInUser = useUserStore((state) => state.user);
  const [loading, setLoading] = useState(true);
  const [showSendMerchantLink, setShowSendMerchantLink] = useState(false);
  const [showGenerateNDownload, setShowGenerateNDownload] = useState(false);
  const [showChangeOwner, setShowChangeOwner] = useState(false);
  const [openCancelConfirmation, setOpenCancelConfirmation] = useState(false);
  const [openEditEmail, setOpenEditEmail] = useState(false);
  const [openEditMobile, setOpenEditMobile] = useState(false);
  const [openAssignProcessor, setOpenAssignProcessor] = useState(false);
  const [confirmCopyApplication, setConfirmCopyApplication] = useState(false);
  const [confirmUpdateProgression, setConfirmUpdateProgression] = useState(false);
  const [selectedProgression, setSelectedProgression] = useState(null);
  const [ehawkData, setEhawkData] = useState(null);
  const [confirmAppDelete, setConfirmAppDelete] = useState(false);
  const [assignMspModal, setAssignMspModal] = useState(false);

  const [cancelSignatureModal, setCancelSignatureModal] = useState(false);
  const [resendForSignatureModal, setResendForSignatureModal] = useState(false);

  const [editZohoModal, setEditZohoModal] = useState(false);
  const [zohoIdType, setZohoIdType] = useState("zoho_id");

  const isPciAdmin =
    loggedInUser?.role == "admin" && loggedInUser?.organization?.id == 1;

  const tabs = [
    {
      label: "Merchant",
      content: <MerchantDetails appData={activeAppData} />,
    },
    {
      label: "Files",
      content: <Files appData={activeAppData} />,
    },
    {
      label: "Fraud Score",
      content: <FraudScore data={ehawkData} />,
      badge: `${ehawkData?.content?.score?.risk !== undefined ? ehawkData?.content?.score?.risk : ""}`,
      hidden: isPciAdmin ? false : true,
    },
    {
      label: "Submissions",
      content: <Submission appData={activeAppData} />,
      badge: "",
      hidden: (activeAppData?.progression_id > 7 && activeAppData?.progression_id < 19 && isPciAdmin) ? false : true,
    },
    {
      label: "Activity",
      content: <Activity activeApp={activeApp} />,
    },
  ];

  const editApplication = () => {
    let link = "";
    if (activeAppData?.user?.channel == "sales_channel") {
      link = `${STRATUS_URL}v2/application/${activeAppData.user.agent_url}/${activeAppData.editslug}`;
    } else {
      link = `${STRATUS_URL}v2/application/${activeAppData.organization.whitelabel_slug}/${activeAppData.user.agent_url}/${activeAppData.editslug}`;
    }
    window.open(link);
  };

  const drop_down_options = [
    {
      text: "Assign Processor",
      type: "normal",
      onClick: () => {
        setOpenAssignProcessor(true);
      },
    },
    {
      text: "Assign MSP",
      type: "normal",
      onClick: () => {
        setAssignMspModal(true);
      },
    },
    {
      text: "Cancel Signature",
      type: "normal",
      onClick: () => {
        setCancelSignatureModal(true);
      },
      hidden:
        activeAppData?.progression_id == awaiting_signature ? false : true,
    },
    {
      text: "Re-Send For Signature",
      type: "normal",
      onClick: () => {
        setResendForSignatureModal(true);
      },
      hidden:
        activeAppData?.progression_id == awaiting_signature ? false : true,
    },
    {
      text: "Change owner",
      type: "normal",
      onClick: () => {
        setShowChangeOwner(true);
      },
    },
    {
      text: "Duplicate Application",
      type: "normal",
      onClick: () => {
        setConfirmCopyApplication(true);
      },
    },
    {
      text: "Edit Zoho ID",
      type: "normal",
      onClick: () => {
        handleEditZoho("zoho_id");
      },
    },
    {
      text: "Edit Submission ID",
      type: "normal",
      onClick: () => {
        handleEditZoho("submission_id");
      },
    },
    {
      text: "Edit Application",
      type: "normal",
      onClick: () => {
        editApplication();
      },
    },
    {
      text: "Edit OTP Email",
      type: "normal",
      onClick: () => {
        setOpenEditEmail(true);
      },
    },
    {
      text: "Edit OTP Mobile",
      type: "normal",
      onClick: () => {
        setOpenEditMobile(true);
      },
    },
    {
      text: "Cancel",
      type: "danger",
      onClick: () => {
        setOpenCancelConfirmation(true);
      },
    },
    {
      text: "Delete",
      type: "danger",
      onClick: () => {
        setConfirmAppDelete(true);
      },
    },
  ];

  const onCloseClick = () => {
    setShowDetails(false);
    setActiveAppData(null);
    if (expandDetails) {
      toggleExpandDetails();
    }
  };

  const fetchEhawkData = async () => {
    const notes_response = await getNotes({ id: activeApp.id, parent_id: "" });
    if (notes_response?.status && notes_response?.ehawk?.length > 0) {
      setEhawkData(notes_response?.ehawk[0]);
    } else {
      setEhawkData(null);
    }
  };

  const fetchApplication = async () => {
    setLoading(true);
    setActiveAppData(null);
    await getPreappById({ id: activeApp.id })
      .then((res) => {
        if (res.status) {
          getMerchantInfo({ id: activeApp.id })
            .then((merchant_response) => {
              if (merchant_response.status) {
                setLoading(false);
                setActiveAppData({
                  ...res.data,
                  bankinfo: merchant_response.bankinfo,
                  creditcardprocess: merchant_response.creditcardprocess,
                  powerappbusinessprofile:
                    merchant_response.powerappbusinessprofile,
                  powerappequipment: merchant_response.powerappequipment,
                  prices: merchant_response.prices,
                  signers: merchant_response.signers,
                  billing: merchant_response.billing,
                  powerappReferral: merchant_response.powerappReferral,
                });
              }
            })
            .catch();
        }
      })
      .catch();
  };

  useEffect(async () => {
    fetchApplication();
    fetchEhawkData();
  }, [JSON.stringify(activeApp)]);

  const progression_options = [
    {
      value: 1,
      label: "Back to Edit",
      color: "orange",
    },
    {
      value: 8,
      label: "Signed - PC Review",
      color: "#819FEC",
    },
    {
      value: 9,
      label: "Underwriting Review",
      color: "#7FCECE",
    },
    {
      value: 10,
      label: "Pended",
      color: "#E7B8DF",
    },
    {
      value: 11,
      label: "Awaiting Reserve",
      color: "#44AE42",
    },
    {
      value: 12,
      label: "Approved",
      color: "#44AE42",
    },
    {
      value: 13,
      label: "Denied",
      color: "#FFA500",
    },
    {
      value: 14,
      label: "Cancelled",
      color: "#660000",
    },
    {
      value: 15,
      label: "App pending PC approval",
      color: "#44AE42",
    },
    {
      value: 17,
      label: "PDF requested",
      color: "#F0BE74",
    },
    {
      value: 18,
      label: "Awaiting wet signature",
      color: "#F0BE74",
    },
    {
      value: 21,
      label: "Terminated",
      color: "#FF0000",
    },
    {
      value: 22,
      label: "Support",
      color: "#0000FF",
    },
  ];

  const shouldShowProgressionSelect = () => {
    if (!activeAppData || !activeAppData.organization || !activeAppData.user)
      return false;

    // Case 1: Sales Channel Admin showing Sales Progression Select
    if (
      activeAppData.user.channel === "sales_channel" &&
      loggedInUser?.channel === "sales_channel" &&
      loggedInUser?.role === "admin" &&
      activeAppData.lead_progression_id > 7
    ) {
      return true;
    }

    // Case 2: PCI Admin showing General Progression Select
    if (
      isPciAdmin &&
      activeAppData?.progression_id > 7 &&
      activeAppData?.progression_id !== 20 &&
      !hide_progression.includes(activeAppData?.progression_id)
    ) {
      return true;
    }

    return false;
  };

  const updateAppProgression = async () => {
    const data = {
      id: activeAppData?.id,
      progression_id: selectedProgression,
      channel: loggedInUser?.channel,
    };
    const toastId = toast.loading("Updating application status...", {
      duration: 30 * 1000,
    });
    const response = await updateProgression(data);

    if (response.status) {
      toast.success("Status updated successfully.", {
        duration: 10 * 1000,
        id: toastId,
      });
      fetchApplication();
      setConfirmUpdateProgression(false);
      setSelectedProgression(null);
    } else {
      toast.error("Something went wrong!", {
        duration: 10 * 1000,
        id: toastId,
      });
    }
  };

  const deleteApplication = async () => {
    const toastID = toast.loading("Deleting apps...");
    const response = await deleteBulkApps({
      bulkDeleteIds: [activeAppData?.id],
    });
    if (response.status) {
      toast.success("Apps deleted successfully", { id: toastID });
      fetchApplication();
      setConfirmAppDelete(false);
      setShowAppDetails(false);
      setActiveApp(null);
    } else {
      toast.error("Something went wrong", { id: toastID });
    }
  };

  const cancelApplication = async () => {
    const toastID = toast.loading("Cancelling apps...");
    const response = await cancelMultipleApp({ ids: [activeAppData?.id] });
    if (response.status) {
      toast.success("Apps cancelled successfully", { id: toastID });
      setOpenCancelConfirmation(false);
      setShowAppDetails(false);
      setActiveApp(null);
    } else {
      toast.error("Something went wrong", { id: toastID });
    }
  };

  const handleCancelSignature = async () => {
    const toastID = toast.loading("Cancelling signature...");
    const response = await cancelSignature({ id: activeAppData?.id, stratus: true });
    if (response.status) {
      toast.success("Signature cancelled successfully", { id: toastID });
      fetchApplication();
      setCancelSignatureModal(false);
    } else {
      toast.error("Something went wrong", { id: toastID });
    }
  };

  const handleResendForSignature = async () => {
    const toastID = toast.loading("Resending for signature...");
    const response = await resendSignature({ id: activeAppData?.id, stratus: true });
    if (response.status) {
      toast.success("Signature resent successfully", { id: toastID });
      fetchApplication();
      setResendForSignatureModal(false);
    } else {
      toast.error("Something went wrong", { id: toastID });
    }
  };

  const handleEditZoho = (type) => {
    setZohoIdType(type);
    setEditZohoModal(true);
  };

  return (
    <div className={style.app_details_container}>
      {!loading && (
        <>
          {/* Panel Header */}
          <div className={style.details_head}>
            {!expandDetails && (
              <button
                className={style.expand_button}
                onClick={toggleExpandDetails}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M12 2.25C10.0716 2.25 8.18657 2.82183 6.58319 3.89317C4.97982 4.96452 3.73013 6.48726 2.99218 8.26884C2.25422 10.0504 2.06114 12.0108 2.43735 13.9021C2.81355 15.7934 3.74215 17.5307 5.10571 18.8943C6.46928 20.2579 8.20656 21.1865 10.0979 21.5627C11.9892 21.9389 13.9496 21.7458 15.7312 21.0078C17.5127 20.2699 19.0355 19.0202 20.1068 17.4168C21.1782 15.8134 21.75 13.9284 21.75 12C21.7473 9.41498 20.7192 6.93661 18.8913 5.10872C17.0634 3.28084 14.585 2.25273 12 2.25ZM14.0306 15.2194C14.1003 15.2891 14.1556 15.3718 14.1933 15.4628C14.231 15.5539 14.2504 15.6515 14.2504 15.75C14.2504 15.8485 14.231 15.9461 14.1933 16.0372C14.1556 16.1282 14.1003 16.2109 14.0306 16.2806C13.9609 16.3503 13.8782 16.4056 13.7872 16.4433C13.6961 16.481 13.5986 16.5004 13.5 16.5004C13.4015 16.5004 13.3039 16.481 13.2128 16.4433C13.1218 16.4056 13.0391 16.3503 12.9694 16.2806L9.21938 12.5306C9.14965 12.461 9.09433 12.3783 9.05658 12.2872C9.01884 12.1962 8.99941 12.0986 8.99941 12C8.99941 11.9014 9.01884 11.8038 9.05658 11.7128C9.09433 11.6217 9.14965 11.539 9.21938 11.4694L12.9694 7.71937C13.0391 7.64969 13.1218 7.59442 13.2128 7.5567C13.3039 7.51899 13.4015 7.49958 13.5 7.49958C13.5986 7.49958 13.6961 7.51899 13.7872 7.5567C13.8782 7.59442 13.9609 7.64969 14.0306 7.71937C14.1003 7.78906 14.1556 7.87178 14.1933 7.96283C14.231 8.05387 14.2504 8.15145 14.2504 8.25C14.2504 8.34855 14.231 8.44613 14.1933 8.53717C14.1556 8.62822 14.1003 8.71094 14.0306 8.78063L10.8103 12L14.0306 15.2194Z"
                    fill="#254FE2"
                  />
                </svg>
                <span>Expand Details</span>
              </button>
            )}

            {expandDetails && (
              <button
                className={style.expand_button}
                onClick={toggleExpandDetails}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M12 2.25C10.0716 2.25 8.18657 2.82183 6.58319 3.89317C4.97982 4.96452 3.73013 6.48726 2.99218 8.26884C2.25422 10.0504 2.06114 12.0108 2.43735 13.9021C2.81355 15.7934 3.74215 17.5307 5.10571 18.8943C6.46928 20.2579 8.20656 21.1865 10.0979 21.5627C11.9892 21.9389 13.9496 21.7458 15.7312 21.0078C17.5127 20.2699 19.0355 19.0202 20.1068 17.4168C21.1782 15.8134 21.75 13.9284 21.75 12C21.7473 9.41498 20.7192 6.93661 18.8913 5.10872C17.0634 3.28084 14.585 2.25273 12 2.25ZM14.7806 12.5306L11.0306 16.2806C10.9609 16.3503 10.8782 16.4056 10.7872 16.4433C10.6961 16.481 10.5986 16.5004 10.5 16.5004C10.4015 16.5004 10.3039 16.481 10.2128 16.4433C10.1218 16.4056 10.0391 16.3503 9.96938 16.2806C9.8997 16.2109 9.84442 16.1282 9.80671 16.0372C9.769 15.9461 9.74959 15.8485 9.74959 15.75C9.74959 15.6515 9.769 15.5539 9.80671 15.4628C9.84442 15.3718 9.8997 15.2891 9.96938 15.2194L13.1897 12L9.96938 8.78063C9.82865 8.63989 9.74959 8.44902 9.74959 8.25C9.74959 8.05098 9.82865 7.86011 9.96938 7.71937C10.1101 7.57864 10.301 7.49958 10.5 7.49958C10.699 7.49958 10.8899 7.57864 11.0306 7.71937L14.7806 11.4694C14.8504 11.539 14.9057 11.6217 14.9434 11.7128C14.9812 11.8038 15.0006 11.9014 15.0006 12C15.0006 12.0986 14.9812 12.1962 14.9434 12.2872C14.9057 12.3783 14.8504 12.461 14.7806 12.5306Z"
                    fill="#254FE2"
                  />
                </svg>
                <span>Minimize</span>
              </button>
            )}

            <ButtonIcon onClick={onCloseClick}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M19.2806 18.2194C19.3503 18.289 19.4056 18.3718 19.4433 18.4628C19.481 18.5539 19.5004 18.6514 19.5004 18.75C19.5004 18.8485 19.481 18.9461 19.4433 19.0372C19.4056 19.1282 19.3503 19.2109 19.2806 19.2806C19.2109 19.3503 19.1282 19.4056 19.0372 19.4433C18.9461 19.481 18.8485 19.5004 18.75 19.5004C18.6515 19.5004 18.5539 19.481 18.4628 19.4433C18.3718 19.4056 18.2891 19.3503 18.2194 19.2806L12 13.0603L5.78062 19.2806C5.63989 19.4213 5.44902 19.5004 5.25 19.5004C5.05097 19.5004 4.8601 19.4213 4.71937 19.2806C4.57864 19.1399 4.49958 18.949 4.49958 18.75C4.49958 18.551 4.57864 18.3601 4.71937 18.2194L10.9397 12L4.71937 5.78061C4.57864 5.63988 4.49958 5.44901 4.49958 5.24999C4.49958 5.05097 4.57864 4.8601 4.71937 4.71936C4.8601 4.57863 5.05097 4.49957 5.25 4.49957C5.44902 4.49957 5.63989 4.57863 5.78062 4.71936L12 10.9397L18.2194 4.71936C18.3601 4.57863 18.551 4.49957 18.75 4.49957C18.949 4.49957 19.1399 4.57863 19.2806 4.71936C19.4214 4.8601 19.5004 5.05097 19.5004 5.24999C19.5004 5.44901 19.4214 5.63988 19.2806 5.78061L13.0603 12L19.2806 18.2194Z"
                  fill="#A4A3A8"
                />
              </svg>
            </ButtonIcon>
          </div>

          <div className={style.application_min_data}>
            {/* DBA name and CTAs */}
            <div className={style.container_min_data}>
              <div className={style.left}>
                <span className={style.dba_name}>{activeApp?.field_dba}</span>
                <AppStatusDropDown
                  options={progression_options}
                  progression_id={activeAppData?.progression_id}
                  onChange={(data) => {
                    setSelectedProgression(data.progression_id);
                    setConfirmUpdateProgression(true);
                  }}
                  disabled={!shouldShowProgressionSelect()}
                />
              </div>

              <div className={style.right}>
                <ButtonIcon
                  bgColor="#EDEDF4"
                  onClick={() => setShowSendMerchantLink(true)}
                >
                  <PaperPlane />
                </ButtonIcon>
                <ButtonIcon
                  bgColor="#EDEDF4"
                  onClick={() => setShowGenerateNDownload(true)}
                >
                  <DownloadIcon />
                  <span>Download</span>
                </ButtonIcon>
                <DropDown options={drop_down_options}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M10.5 5.625C10.5 5.32833 10.588 5.03832 10.7528 4.79165C10.9176 4.54497 11.1519 4.35271 11.426 4.23918C11.7001 4.12565 12.0017 4.09594 12.2926 4.15382C12.5836 4.2117 12.8509 4.35456 13.0607 4.56434C13.2704 4.77412 13.4133 5.04139 13.4712 5.33237C13.5291 5.62334 13.4994 5.92494 13.3858 6.19903C13.2723 6.47311 13.08 6.70738 12.8334 6.87221C12.5867 7.03703 12.2967 7.125 12 7.125C11.6022 7.125 11.2206 6.96697 10.9393 6.68566C10.658 6.40436 10.5 6.02283 10.5 5.625ZM12 10.5C11.7033 10.5 11.4133 10.588 11.1666 10.7528C10.92 10.9176 10.7277 11.1519 10.6142 11.426C10.5006 11.7001 10.4709 12.0017 10.5288 12.2926C10.5867 12.5836 10.7296 12.8509 10.9393 13.0607C11.1491 13.2704 11.4164 13.4133 11.7074 13.4712C11.9983 13.5291 12.2999 13.4994 12.574 13.3858C12.8481 13.2723 13.0824 13.08 13.2472 12.8334C13.412 12.5867 13.5 12.2967 13.5 12C13.5 11.6022 13.342 11.2206 13.0607 10.9393C12.7794 10.658 12.3978 10.5 12 10.5ZM12 16.875C11.7033 16.875 11.4133 16.963 11.1666 17.1278C10.92 17.2926 10.7277 17.5269 10.6142 17.801C10.5006 18.0751 10.4709 18.3767 10.5288 18.6676C10.5867 18.9586 10.7296 19.2259 10.9393 19.4357C11.1491 19.6454 11.4164 19.7883 11.7074 19.8462C11.9983 19.9041 12.2999 19.8744 12.574 19.7608C12.8481 19.6473 13.0824 19.455 13.2472 19.2084C13.412 18.9617 13.5 18.6717 13.5 18.375C13.5 17.9772 13.342 17.5956 13.0607 17.3143C12.7794 17.033 12.3978 16.875 12 16.875Z"
                      fill="#3A383F"
                    />
                  </svg>
                </DropDown>
              </div>
            </div>

            {/* App primary details */}
            <div className={`${style.app_details} ${expandDetails ? style.expanded : ""}`}>
              <div className={style.data_item}>
                <span>App ID</span>
                <div>
                  <span>{activeApp.id}</span>
                  <Copier text={activeApp.id}>
                    <CopyIcon />
                  </Copier>
                </div>
              </div>

              <div className={style.data_item}>
                <span>Zoho ID</span>
                <div>
                  <span>{activeApp.zoho_id || "-"}</span>
                  <div className={style.actions}>
                    {activeApp.zoho_id && (
                      <Copier text={activeApp.zoho_id}>
                        <CopyIcon />
                      </Copier>
                    )}
                  </div>
                </div>
              </div>

              <div className={style.data_item}>
                <span>Submission ID</span>
                <div>
                  <span>{activeApp.zoho_submission_id || "-"}</span>
                  <div className={style.actions}>
                    {activeApp.zoho_submission_id && (
                      <Copier text={activeApp.zoho_submission_id}>
                        <CopyIcon />
                      </Copier>
                    )}
                  </div>
                </div>
              </div>

              {expandDetails && (
                <>
                  <div className={style.data_item}>
                    <span>MID</span>
                    <div>
                      <span>{activeAppData?.power_app_submission?.mid || '-'}</span>
                      <Copier text="Agent/ISO">
                        <CopyIcon />
                      </Copier>
                    </div>
                  </div>

                  <div className={style.data_item}>
                    <span>Merchant Legal</span>
                    <div>
                      <span>{activeAppData?.field_legalname || '-'}</span>
                      <Copier text="Agent/ISO">
                        <CopyIcon />
                      </Copier>
                    </div>
                  </div>
                </>
              )}


              <div className={style.data_item}>
                <span>Bank Name</span>
                <div>
                  <span>{activeAppData?.bank?.name || '-'}</span>
                  <Copier text={activeAppData?.bank?.name || '-'}>
                    <CopyIcon />
                  </Copier>
                </div>
              </div>

              <div className={style.data_item}>
                <span>Organization</span>
                <div>
                  <span>{activeAppData?.organization?.name || "-"}</span>
                  <Copier text={activeAppData?.organization?.name || "-"} >
                    <CopyIcon />
                  </Copier>
                </div>
              </div>

              {expandDetails && (
                <div className={style.data_item}>
                  <span>Org Type</span>
                  <div>
                    <span>{activeAppData?.organization?.type ? (activeAppData?.organization?.type == 'hands_on' ? 'Agent/ISO' : 'Referral Partner') : '-'} </span>
                    <Copier text={activeAppData?.organization?.type ? (activeAppData?.organization?.type == 'hands_on' ? 'Agent/ISO' : 'Referral Partner') : '-'}>
                      <CopyIcon />
                    </Copier>
                  </div>
                </div>
              )}


              <div className={style.data_item}>
                <span>Agent</span>
                <div>
                  <div className={style.name_f_letter}>
                    {activeAppData?.user?.name[0]}
                  </div>
                  <p>{activeAppData?.user?.name}</p>
                </div>
              </div>

              {expandDetails && (
                <>
                  <div className={style.data_item}>
                    <span>Team Lead</span>
                    <div>
                      <span>{activeAppData?.organization?.get_primary_contact[0]?.name || '-'}</span>
                      <Copier text="Agent/ISO">
                        <CopyIcon />
                      </Copier>
                    </div>
                  </div>


                  <div className={style.data_item}>
                    <span>Underwriting</span>
                    <div>
                      <span>{activeAppData?.organization?.uw_data?.name || '-'}</span>
                      <Copier text="Agent/ISO">
                        <CopyIcon />
                      </Copier>
                    </div>
                  </div>
                </>
              )}

            </div>
            {/* Tabs */}
            <div>
              <Tabs tabs={tabs} />
            </div>
          </div>
          {/* Modals */}
          {showSendMerchantLink && (
            <SendMerchantLink
              open={showSendMerchantLink}
              onClose={() => setShowSendMerchantLink(false)}
            />
          )}
          {showGenerateNDownload && (
            <GenerateDownload
              open={showGenerateNDownload}
              onClose={() => setShowGenerateNDownload(false)}
            />
          )}
          {showChangeOwner && (
            <ChangeOwner
              open={showChangeOwner}
              onClose={() => setShowChangeOwner(false)}
              fetchApplication={fetchApplication}
            />
          )}
          {openCancelConfirmation && (
            <ConfirmationModal
              open={openCancelConfirmation}
              onClose={() => setOpenCancelConfirmation(false)}
              message={
                "Are you sure you want to cancel this app? You can't undo this action."
              }
              onConfirmClick={() => cancelApplication()}
            />
          )}
          {openEditEmail && (
            <EditEmail
              open={openEditEmail}
              onClose={() => setOpenEditEmail(false)}
              appData={activeAppData}
              fetchApplication={fetchApplication}
            />
          )}
          {openEditMobile && (
            <EditMobile
              open={openEditMobile}
              onClose={() => setOpenEditMobile(false)}
              appData={activeAppData}
              fetchApplication={fetchApplication}
            />
          )}
          {openAssignProcessor && (
            <AssignProcessor
              open={openAssignProcessor}
              onClose={() => setOpenAssignProcessor(false)}
            />
          )}
          {/* Duplicate confirmation */}
          {confirmCopyApplication && (
            <CopyApplication
              appData={activeAppData}
              isOpen={confirmCopyApplication}
              onClose={() => setConfirmCopyApplication(false)}
              loggedInUser={loggedInUser}
            />
          )}
          {/* Progression confirmation */}
          {confirmUpdateProgression && (
            <ConfirmationModal
              open={confirmUpdateProgression}
              onClose={() => setConfirmUpdateProgression(false)}
              message={
                "Are you sure! you want to update progression for selected app(s)?"
              }
              onConfirmClick={() => updateAppProgression()}
            />
          )}
          {/* delete confirmation */}
          {confirmAppDelete && (
            <ConfirmationModal
              open={confirmAppDelete}
              onClose={() => setConfirmAppDelete(false)}
              message={
                "Are you sure you want to Delete this app? You can't undo this action."
              }
              onConfirmClick={() => deleteApplication()}
            />
          )}
          {/* Assign MSP */}
          {assignMspModal && (
            <AssignMSP
              open={assignMspModal}
              onClose={() => setAssignMspModal(false)}
              appData={activeAppData}
            />
          )}
          {cancelSignatureModal && (
            <ConfirmationModal
              open={cancelSignatureModal}
              onClose={() => setCancelSignatureModal(false)}
              message={
                "Canceling signature process will reset everything and return you to the editing. Are you sure you want Cancel signature ?"
              }
              onConfirmClick={() => handleCancelSignature()}
            />
          )}
          {resendForSignatureModal && (
            <ConfirmationModal
              open={resendForSignatureModal}
              onClose={() => setResendForSignatureModal(false)}
              message={
                "Are you sure you want to resend signature?"
              }
              onConfirmClick={() => handleResendForSignature()}
            />
          )}
          {editZohoModal && (
            <EditZoho
              open={editZohoModal}
              onClose={() => setEditZohoModal(false)}
              appData={activeAppData}
              getAppData={fetchApplication}
              zohoIdType={zohoIdType}
            />
          )}
        </>
      )}

      {loading && (
        <div className={style.loader_section}>
          <Spinner />
        </div>
      )}
    </div>
  );
}

export default ApplicationDetails;
